// recaptcha.ts

declare global {
  interface Window {
    grecaptcha?: any;
    __caRecaptchaOnload?: () => void;
  }
}

// Promesse unique partagée par tous les formulaires : un second appel pendant le chargement
// attend le même script au lieu de répondre « chargé » trop tôt.
let recaptchaPromise: Promise<boolean> | null = null;

export const loadRecaptchaScript = (): Promise<boolean> => {
  if (recaptchaPromise) return recaptchaPromise;

  recaptchaPromise = new Promise((resolve) => {
    if (typeof window.grecaptcha?.render === 'function') {
      resolve(true);
      return;
    }

    console.log("Chargement du script reCAPTCHA...");
    // Le callback onload garantit que grecaptcha.render est utilisable (le simple
    // événement load du script arrive avant). Tous les widgets sont rendus explicitement.
    window.__caRecaptchaOnload = () => {
      console.log("✓ Script reCAPTCHA chargé avec succès");
      resolve(true);
    };

    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js?onload=__caRecaptchaOnload&render=explicit';
    script.async = true;
    script.defer = true;

    const fail = (message: string) => {
      console.error(message);
      script.remove();
      recaptchaPromise = null; // autoriser une nouvelle tentative
      resolve(false);
    };
    script.onerror = () => fail("❌ Erreur lors du chargement du script reCAPTCHA");
    // Si le callback onload n'arrive jamais (script interne de Google bloqué), répondre false
    // plutôt que laisser la modale attendre indéfiniment sans message d'erreur.
    script.onload = () => setTimeout(() => {
      if (typeof window.grecaptcha?.render !== 'function') fail("❌ reCAPTCHA chargé mais inutilisable");
    }, 10000);

    document.head.appendChild(script);
  });

  return recaptchaPromise;
};

// reCAPTCHA pèse ~300 Ko : on ne le charge qu'à la première interaction avec le formulaire,
// pas à l'ouverture de la page. Le rendu du widget attend ensuite loadRecaptchaScript().
export const loadRecaptchaOnInteraction = (target: EventTarget): void => {
  const events = ['focusin', 'pointerdown', 'touchstart'];
  const trigger = () => {
    events.forEach((type) => target.removeEventListener(type, trigger));
    void loadRecaptchaScript();
  };
  events.forEach((type) => target.addEventListener(type, trigger, { passive: true }));
};

export const verifyRecaptcha = async (): Promise<boolean> => {
  try {
    if (typeof window.grecaptcha === 'undefined') {
      console.error("grecaptcha n'est pas défini");
      return false;
    }
    
    const response = window.grecaptcha.getResponse();
    if (response.length === 0) {
      console.warn("reCAPTCHA non validé côté client");
      return false;
    }

    console.log("✓ reCAPTCHA validé côté client");
    return true;
  } catch (error) {
    console.error("Erreur reCAPTCHA:", error);
    return false;
  }
};